import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import type { SamModule } from '@/components/SAM/samActionTypes';
import type { AppThunk } from '@/redux/common/store';
import { refreshNotesThunk } from './notesApi';
import { workflowEngineApi } from './workflowEngineApi';

// ─── Response envelopes (match the API Response Standard) ──────────────────────
interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

/** url path segment → RTK cache tag, for invalidation after mutations. */
const PATH_TAG = {
  properties: 'Property',
  transactions: 'Transaction',
  tasks: 'Task',
  proposals: 'Proposal',
  invoices: 'Invoice',
  meetings: 'Meeting',
  contacts: 'Contact',
  jobs: 'Job',
  calendar: 'CalendarEvent',
} as const;

export type ResourcePath = keyof typeof PATH_TAG;

/** SAM module → REST path + cache tag, for the Action Router's generic fetch.
 *  users/roles default to the tenant-scoped endpoints; the chat hook overrides the path
 *  with the platform endpoint when the caller is a platform admin. */
const MODULE_PATH: Record<string, string> = {
  tasks: 'tasks', notes: 'notes', properties: 'properties', meetings: 'meetings', transactions: 'transactions',
  proposals: 'proposals', jobs: 'jobs', calendar: 'meetings/calendar', billing: 'invoices', contacts: 'contacts',
  users: 'users', roles: 'roles/tenant', communications: 'communications',
  my_todos: 'tasks/my-todos',
};
const MODULE_TAG: Record<string, string> = {
  tasks: 'Task', properties: 'Property', meetings: 'Meeting', transactions: 'Transaction',
  proposals: 'Proposal', jobs: 'Job', calendar: 'CalendarEvent', billing: 'Invoice', contacts: 'Contact',
  users: 'User', roles: 'Role', communications: 'Communication', my_todos: 'Task',
};

// ─── Domain types (subset of the seeded document shapes) ───────────────────────
export interface DashboardSummary {
  counts: {
    properties: number;
    transactions: number;
    tasks: number;
    proposals: number;
    invoices: number;
    meetings: number;
    contacts: number;
    jobs: number;
    calendar_events: number;
  };
  metrics: {
    pending_tasks: number;
    active_transactions: number;
    scheduled_jobs: number;
    pipeline_value: number;
    outstanding_invoices: number;
  };
}

export interface PropertyAddress {
  street_number: string;
  pre_direction: string | null;
  street_name: string;
  street_suffix: string;
  post_direction: string | null;
  unit_type: string | null;
  unit_number: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string | null;
  normalized_address: string;
  // Backend-computed display line (e.g. "123 N Main St, Austin, TX 78701") — optional since
  // it's only populated once the backend serializes it via @computed_field; older cached
  // responses or partial views may not carry it.
  display_line?: string;
}
export interface Property {
  property_id: string;
  address: PropertyAddress;
  property_type: string;
  owner_name: string;
  contact_ids: string[];
  tags: string[];
  status: string;
}

export interface Transaction {
  transaction_id: string;
  property_id: string;
  title: string;
  description: string;
  transaction_type: string;
  source: string;
  value: number;
  currency: string;
  workflow: { stage: string; process: string; status: string };
  active: boolean;
  start_date: string | null;
  target_date: string | null;
}

export interface Task {
  task_id: string;
  title: string;
  description: string;
  task_type: string;
  status: string;
  priority: string;
  due_date: string | null;
  linked_context: {
    property_id: string | null;
    transaction_id: string | null;
    contact_id: string | null;
  };
}

export interface ProposalItem {
  description: string;
  unit: string;
  qty: number;
  unit_price: number;
  total: number;
}
export interface Proposal {
  proposal_id: string;
  property_id: string;
  transaction_id: string;
  title: string;
  status: string;
  items: ProposalItem[];
  total: number;
  currency: string;
  valid_until: string | null;
}

export interface Invoice {
  invoice_id: string;
  property_id: string;
  transaction_id: string;
  title: string;
  invoice_number: string;
  status: string;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  issued_date: string | null;
  due_date: string | null;
  paid_date: string | null;
}

export interface Meeting {
  meeting_id: string;
  property_id: string;
  transaction_id: string;
  title: string;
  meeting_type: string;
  status: string;
  attendees: string[];
  scheduled_at: string | null;
  duration_minutes: number;
  location: string;
  notes: string;
}

export interface Contact {
  contact_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  contact_type: string;
  company_name: string | null;
  roles: string[];
  preferred_contact_method: string;
}

export interface JobCrewMember {
  user_id?: string;
  name?: string;
  role: string;
}
export interface Job {
  job_id: string;
  property_id: string;
  transaction_id: string;
  title: string;
  job_type: string;
  status: string;
  crew: JobCrewMember[];
  crew_size: number;
  scheduled_start: string | null;
  scheduled_end: string | null;
  address: string;
  notes: string;
}

export interface CalendarEvent {
  calendar_event_id: string;
  title: string;
  event_type: string;
  status: string;
  start: string | null;
  end: string | null;
  all_day: boolean;
  location: string | null;
  attendees: string[];
  notes: string;
  related: {
    property_id: string | null;
    transaction_id: string | null;
    meeting_id: string | null;
    job_id: string | null;
  };
}

export const workspaceApi = createApi({
  reducerPath: 'workspaceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'DashboardSummary',
    'Property',
    'Transaction',
    'Task',
    'Proposal',
    'Invoice',
    'Meeting',
    'Contact',
    'Job',
    'CalendarEvent',
    'User',
    'Role',
    'Communication',
    'Reminder',
  ],
  endpoints: (builder) => ({
    getDashboardSummary: builder.query<DashboardSummary, void>({
      query: () => '/api/v1/dashboard/summary',
      transformResponse: (res: ApiResponse<DashboardSummary>) => res.data,
      providesTags: ['DashboardSummary'],
    }),

    getProperties: builder.query<Property[], { search?: string } | void>({
      query: (arg) => {
        const search = arg && 'search' in arg ? arg.search : undefined;
        return search ? `/api/v1/properties?search=${encodeURIComponent(search)}` : '/api/v1/properties';
      },
      transformResponse: (res: ApiListResponse<Property>) => res.data,
      providesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    getTransactions: builder.query<Transaction[], void>({
      query: () => '/api/v1/transactions',
      transformResponse: (res: ApiListResponse<Transaction>) => res.data,
      providesTags: ['Transaction'],
    }),

    getTasks: builder.query<Task[], { status?: string } | void>({
      query: (arg) => {
        const status = arg && 'status' in arg ? arg.status : undefined;
        return status ? `/api/v1/tasks?status=${encodeURIComponent(status)}` : '/api/v1/tasks';
      },
      transformResponse: (res: ApiListResponse<Task>) => res.data,
      providesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    getProposals: builder.query<Proposal[], void>({
      query: () => '/api/v1/proposals',
      transformResponse: (res: ApiListResponse<Proposal>) => res.data,
      providesTags: ['Proposal'],
    }),

    getInvoices: builder.query<Invoice[], void>({
      query: () => '/api/v1/invoices',
      transformResponse: (res: ApiListResponse<Invoice>) => res.data,
      providesTags: ['Invoice'],
    }),

    getMeetings: builder.query<Meeting[], void>({
      query: () => '/api/v1/meetings',
      transformResponse: (res: ApiListResponse<Meeting>) => res.data,
      providesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    getContacts: builder.query<Contact[], void>({
      query: () => '/api/v1/contacts',
      transformResponse: (res: ApiListResponse<Contact>) => res.data,
      providesTags: ['Contact'],
    }),

    getJobs: builder.query<Job[], void>({
      query: () => '/api/v1/jobs',
      transformResponse: (res: ApiListResponse<Job>) => res.data,
      providesTags: ['Job'],
    }),

    getCalendarEvents: builder.query<CalendarEvent[], void>({
      query: () => '/api/v1/calendar',
      transformResponse: (res: ApiListResponse<CalendarEvent>) => res.data,
      providesTags: ['CalendarEvent'],
    }),

    // ── Action-framework generic fetch: module + filter → fresh rows from REST ──────
    // SAM actions carry only {module, filter}; the UI calls this to fetch the data itself
    // (no raw records over the socket). Filter keys become query params and are applied
    // by the per-module service on the backend.
    getModuleRecords: builder.query<unknown[], { module: string; filter?: Record<string, unknown>; path?: string }>({
      query: ({ module, filter, path: pathOverride }) => {
        const path = pathOverride || MODULE_PATH[module] || module;
        const params = new URLSearchParams();
        Object.entries(filter || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
        });
        const qs = params.toString();
        return `/api/v1/${path}${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: ApiListResponse<unknown>) => res.data,
      providesTags: (_r, _e, { module }) => {
        const tag = MODULE_TAG[module];
        return tag ? [tag as never] : [];
      },
    }),

    // ── Generic CRUD (shared across every resource) ────────────────────────
    createResource: builder.mutation<unknown, { path: ResourcePath; body: Record<string, unknown> }>({
      query: ({ path, body }) => ({ url: `/api/v1/${path}`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<unknown>) => res.data,
      invalidatesTags: (_r, _e, { path }) => ['DashboardSummary', PATH_TAG[path]],
    }),

    updateResource: builder.mutation<unknown, { path: ResourcePath; id: string; body: Record<string, unknown> }>({
      query: ({ path, id, body }) => ({ url: `/api/v1/${path}/${id}`, method: 'PUT', body }),
      transformResponse: (res: ApiResponse<unknown>) => res.data,
      invalidatesTags: (_r, _e, { path }) => ['DashboardSummary', PATH_TAG[path]],
    }),

    deleteResource: builder.mutation<unknown, { path: ResourcePath; id: string }>({
      query: ({ path, id }) => ({ url: `/api/v1/${path}/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { path }) => ['DashboardSummary', PATH_TAG[path]],
    }),
  }),
});

// ─── Workspace refresh infrastructure ─────────────────────────────────────────
//
// IMPORTANT — when adding a sub-resource to the Property module (e.g. inspections),
// add its tag to propertyRefreshTags below so the workspace Refresh button covers it.
// When adding a new RTK-Query-backed workspace module, add a resolver to
// refreshTagResolvers — the derived SupportedWorkspaceModule type extends automatically.
// Without a resolver, the Refresh button emits a dev-mode console.warn and silently
// no-ops rather than dispatching stale tags.

type WorkspaceApiTagType =
  | 'DashboardSummary' | 'Property' | 'Transaction' | 'Task' | 'Proposal'
  | 'Invoice' | 'Meeting' | 'Contact' | 'Job' | 'CalendarEvent'
  | 'User' | 'Role' | 'Communication' | 'Reminder';

type WorkspaceApiTagDesc = WorkspaceApiTagType | { type: WorkspaceApiTagType; id: string };
// Each resolver returns an AppThunk so cross-slice invalidations (e.g. notesApi) are supported
// alongside the standard workspaceApi tag invalidations.
type RefreshResolver = (id?: string | null) => AppThunk;

const propertyRefreshThunk: RefreshResolver = (id) => (dispatch) => {
  dispatch(workspaceApi.util.invalidateTags((
    id
      ? [
          { type: 'Property', id },
          { type: 'Property', id: `${id}-media` },
          { type: 'Property', id: `${id}-warranties` },
          { type: 'Property', id: `${id}-measurements` },
          { type: 'Property', id: `${id}-notes` },
          // Task checklist inside each engagement tab — getTasksList provides 'Task/LIST'
          { type: 'Task', id: 'LIST' },
        ]
      : [{ type: 'Property', id: 'LIST' }, { type: 'Task', id: 'LIST' }]
  ) as WorkspaceApiTagDesc[]));
  // Also invalidate all engagement-related data — the property detail renders
  // engagement tabs, workflow state, transitions, and activity log, so a full
  // refresh should reload all of those too.
  dispatch(workflowEngineApi.util.invalidateTags([
    'EngagementList',
    'EngagementActivity',
    'EngagementWorkflowState',
    'AvailableTransitions',
  ]));
};

const taskRefreshThunk: RefreshResolver = (id) => (dispatch) =>
  dispatch(workspaceApi.util.invalidateTags((
    id ? [{ type: 'Task', id }] : [{ type: 'Task', id: 'LIST' }]
  ) as WorkspaceApiTagDesc[]));

const meetingRefreshThunk: RefreshResolver = (id) => (dispatch) =>
  dispatch(workspaceApi.util.invalidateTags((
    id ? [{ type: 'Meeting', id }] : [{ type: 'Meeting', id: 'LIST' }]
  ) as WorkspaceApiTagDesc[]));

const engagementRefreshThunk: RefreshResolver = (id) => (dispatch) => {
  dispatch(workflowEngineApi.util.invalidateTags(
    id
      ? [
          { type: 'Engagement', id },
          { type: 'EngagementWorkflowState', id },
          { type: 'AvailableTransitions', id },
          { type: 'EngagementActivity', id },
        ]
      : ['EngagementList'],
  ));
};

const refreshTagResolvers = {
  properties:  propertyRefreshThunk,
  tasks:       taskRefreshThunk,
  meetings:    meetingRefreshThunk,
  notes:       refreshNotesThunk,
  engagements: engagementRefreshThunk,
} satisfies Record<string, RefreshResolver>;

// Derived from the map — adding a resolver above automatically extends this type.
export type SupportedWorkspaceModule = keyof typeof refreshTagResolvers;

const SUPPORTED_MODULE_SET = new Set<string>(Object.keys(refreshTagResolvers));

function isSupportedWorkspaceModule(module: SamModule): module is SupportedWorkspaceModule {
  return SUPPORTED_MODULE_SET.has(module);
}

/** Dispatch to manually refresh the workspace panel for `module`.
 *  Pass `id` when a detail is open (refreshes the detail + its sub-lists);
 *  omit or pass null/undefined to refresh the list view. */
export const refreshWorkspaceModule = (
  module: SamModule,
  id?: string | null,
): AppThunk => (dispatch) => {
  if (!isSupportedWorkspaceModule(module)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[workspace] no refresh strategy registered for module '${module}'`);
    }
    return;
  }
  dispatch(refreshTagResolvers[module](id));
};

export const {
  useGetDashboardSummaryQuery,
  useGetPropertiesQuery,
  useGetTransactionsQuery,
  useGetTasksQuery,
  useGetProposalsQuery,
  useGetInvoicesQuery,
  useGetMeetingsQuery,
  useGetContactsQuery,
  useGetJobsQuery,
  useGetCalendarEventsQuery,
  useGetModuleRecordsQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,
} = workspaceApi;
